'use client';

import { subscribeToBrowserClickCapture } from '@nextmar/snowplow-sdk';
import { createZeeloolSnowplowDataAdapters } from '@/reporting/integrations/snowplow-sdk/adapters';
import { captureBrowserAnalytics } from '@/reporting/reliability/browserAnalytics';
import { withSpmPageRefer } from '@/reporting/spm/spm-session';
import { isInZeloolApp } from '@/utils/tools';
import { getZeeloolSnowplowClient } from '@/reporting/integrations/snowplow-sdk/client';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';

/** PageView 路由去重时忽略的内部登录参数。 */
const PAGE_VIEW_IGNORED_QUERY_PARAMS = ['showLogin', 'from', 'reason'] as const;

/** 最近一次已安排的 PageView 候选；accepted 后继续承担连续同 key 去重。 */
let lastScheduledPageView: PageViewCandidate | null = null;

/** 最近一次被 SDK 接受且仍是最新访问的 PageView 候选。 */
let lastAcceptedPageView: PageViewCandidate | null = null;

/** PageView 候选的规范化路由。 */
interface PageViewCandidate {
  routeKey: string;
}

/** 浏览器当前路由去重所需的最小快照。 */
interface BrowserRouteSnapshot {
  pathname: string;
  search: string;
}

/** 去除 search 中可能混入的 hash，避免锚点变化触发 PageView。 */
function removeHashFragment(search: string): string {
  return search.split('#')[0] ?? '';
}

/** 过滤内部登录参数，同时保留其他业务 query。 */
function normalizePageViewSearch(search: string): string {
  const cleanSearch = removeHashFragment(search);

  // 空查询无需创建 URLSearchParams。
  if (!cleanSearch) return '';

  const queryText = cleanSearch.startsWith('?') ? cleanSearch.slice(1) : cleanSearch;

  // 只有问号时仍视为无查询。
  if (!queryText) return '';

  const params = new URLSearchParams(queryText);

  // 始终走同一序列化分支，避免 ignored 参数出现前后的 %20/+ 编码差异。
  PAGE_VIEW_IGNORED_QUERY_PARAMS.forEach((paramName) => {
    params.delete(paramName);
  });

  // URLSearchParams 保留其他业务参数的顺序与重复值。
  return params.toString();
}

/** 基于同一时刻的 pathname 与 search 构建 PageView route key。 */
function buildPageViewRouteKey(
  fallbackPathname: string | null,
  snapshot: BrowserRouteSnapshot | null,
): string | null {
  const pathname = snapshot?.pathname || fallbackPathname;

  // pathname 尚未就绪时不安排 PageView。
  if (!pathname) return null;

  const normalizedSearch = normalizePageViewSearch(snapshot?.search ?? '');

  // 没有业务 query 时仅使用 pathname。
  if (!normalizedSearch) return pathname;

  return `${pathname}${normalizedSearch.startsWith('?') ? normalizedSearch : `?${normalizedSearch}`}`;
}

/** 读取同一次浏览器 location 的 pathname 与 search。 */
function getBrowserRouteSnapshot(): BrowserRouteSnapshot | null {
  // SSR 环境不访问浏览器全局。
  if (typeof window === 'undefined') return null;

  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

/** 完成一个 PageView ticket，并仅在 accepted:true 时记录成功。 */
function settlePageViewCandidate(candidate: PageViewCandidate, accepted: boolean): void {
  // 旧 Promise 结束时不能覆盖或清掉后续访问的候选状态。
  if (lastScheduledPageView !== candidate) return;

  // accepted:true 保留最新候选以去重连续事件。
  if (accepted) {
    lastAcceptedPageView = candidate;
  } else {
    // 失败只清理当前候选，使同 route 后续事件能够重试。
    lastScheduledPageView = null;
    lastAcceptedPageView = null;
  }
}

/**
 * App 保留原生 PV 与 SPM 来源；浏览器 PV 由 Sensors 自动镜像拥有，点击沿用原订阅。
 * @returns 无入参；返回 void，按 React 生命周期安装监听并提供清理函数。
 */
export function useSnowplowSdkLifecycle(): void {
  const pathname = usePathname();

  /** 无入参；仅 App 按规范化 route key 发送原生 PV，返回 void。 */
  const trackCurrentPageView = useCallback((): void => {
    if (!isInZeloolApp()) return;
    const client = getZeeloolSnowplowClient();

    // App 保留既有 SDK 入口；浏览器捕获不等待 singleton 配置或 Tracker ready。
    if (!client) return;

    const snapshot = getBrowserRouteSnapshot();

    const routeKey = buildPageViewRouteKey(pathname, snapshot);

    // pathname 未就绪时不安排 PageView。
    if (!routeKey) return;

    const latestRouteWasAccepted =
      lastAcceptedPageView === lastScheduledPageView && lastAcceptedPageView?.routeKey === routeKey;
    const latestRouteIsPending =
      lastScheduledPageView?.routeKey === routeKey && !latestRouteWasAccepted;

    // 连续同 key 无论仍待返回还是已经接受，都不能重复发送。
    if (latestRouteWasAccepted || latestRouteIsPending) return;

    // 页面级来源 spm：无来源时保持原始载荷形状（不拼空 properties）。
    const pageViewProps = withSpmPageRefer();

    const candidate: PageViewCandidate = {
      routeKey,
    };
    lastScheduledPageView = candidate;

    try {
      const resultPromise = pageViewProps
        ? client.trackPageView(pageViewProps)
        : client.trackPageView();

      // rejected Promise 必须消费；accepted:false 释放同 route 的后续重试资格。
      void resultPromise
        .then((result) => {
          settlePageViewCandidate(candidate, result.accepted === true);
        })
        .catch(() => {
          settlePageViewCandidate(candidate, false);
        });
    } catch {
      // 同步异常同样只释放当前 ticket，不影响页面渲染或后续重试。
      settlePageViewCandidate(candidate, false);
    }
  }, [pathname]);

  // 首屏和 usePathname 变化均检查当前 route key。
  useEffect(() => {
    trackCurrentPageView();
  }, [trackCurrentPageView]);

  // 复用 PathnameTracker 已派发的历史事件，不再次包装 history 方法。
  useEffect(() => {
    if (typeof window === 'undefined' || !isInZeloolApp()) return;

    /** 将历史事件转换为一次当前 route key 检查。 */
    const handleRouteChange = (): void => {
      trackCurrentPageView();
    };
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('zl-history-change', handleRouteChange);

    /** 卸载当前 Hook 时移除两个浏览器历史监听。 */
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('zl-history-change', handleRouteChange);
    };
  }, [trackCurrentPageView]);

  // 自动点击的 DOM 提取、监听与共享生命周期全部交给 SDK。
  useEffect(() => {
    try {
      // 浏览器从首次 effect 起复用 SDK 同一个共享监听，配置后不另装发送监听。
      if (!isInZeloolApp()) {
        return subscribeToBrowserClickCapture(
          createZeeloolSnowplowDataAdapters(),
          (properties, context) => {
            // SDK 已在点击发生时冻结上下文，必须原样传入同一捕获队列。
            captureBrowserAnalytics(
              { kind: 'webClick', properties: withSpmPageRefer(properties) },
              { context },
            );
          },
        );
      }

      // App 保留原有 SDK 自动点击及其 cleanup 合同。
      return getZeeloolSnowplowClient()?.startAutoClickTracking();
    } catch {
      // 自动点击启动异常不影响 PageView 与页面交互。
      return;
    }
  }, []);
}
